import { TravelDisplay as TravelComponent } from "@pages/booking/cart/travel/Travel";
import { useEffect, useState } from "react";
import { Icon, Button, Skeleton, Modal } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { CartItem } from "@gen/travel/v1/cart_pb";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";
import { useNavigate } from "react-router";
import { routes } from "@routes";
import { useModal } from "@hooks/useModal";
import { useCartStore } from "@stores/cartStore";

export function Cart() {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const { isOpen, openModal, closeModal } = useModal();
  const { cart, fetchCart, release, isLoading } = useCartStore();
  const [isEmptyingCart, setIsEmptyingCart] = useState(false);

  async function handleEmptyCart() {
    if (!cart) return;
    closeModal();
    setIsEmptyingCart(true);
    try {
      for (const item of cart) {
        if (item.travel?.id) {
          await release(item.travel);
        }
      }
    } catch (e: unknown) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsEmptyingCart(false);
    }
  }

  useEffect(() => {
    fetchCart();
  }, []);

  return (
    <div className="flex flex-col gap-6 width-full mb-20">
      {cart && cart.length > 0 && (
        <div className="flex justify-between gap-6 flex-col lg:flex-row lg:items-center">
          <div className="text-[24px] lg:text-[32px] font-bold font-jakarta text-text-strong">
            {t("cart.myCart")}
          </div>
          <Modal
            isOpen={isOpen}
            onClose={closeModal}
            onConfirm={handleEmptyCart}
            content={{
              type: "confirm",
              title: t("cart.deleteConfirmation"),
            }}
            size="default"
          />
          <div className="flex gap-4">
            <Button
              kind="outline"
              size="compact"
              disabled={isLoading}
              onClick={openModal}
              isLoading={isEmptyingCart}
              startEnhancer={<Icon src="trash" color="#BD0039" />}
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    border: `1px ${$theme.colors.red} solid`,
                    color: $theme.colors.red,
                    backgroundColor: "transparent",
                    fontWeight: "bold",
                    ":hover": {
                      backgroundColor: "rgba(189, 0, 57, 0.05)",
                    },
                  }),
                },
              }}
            >
              {t("cart.emptyCart")}
            </Button>
            <Button
              kind="outline"
              size="compact"
              disabled={isLoading}
              onClick={() => navigate(`${routes.SEARCH}`)}
              startEnhancer={<Icon src="add_line" color="#0C419A" />}
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    border: `1px ${$theme.colors.blue} solid`,
                    color: $theme.colors.blue,
                    backgroundColor: "transparent",
                    fontWeight: "bold",
                    ":hover": {
                      backgroundColor: "rgba(12, 65, 154, 0.05)",
                    },
                  }),
                },
              }}
            >
              {t("cart.addTrip")}
            </Button>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Skeleton animation width="100%" height="400px" />
        </div>
      ) : cart && cart.length === 0 ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-8">
          <div className="flex flex-col items-center justify-center ">
            <Icon size={100} src="basket_fill" color="#9CA8C8" />
            <div className="font-jakarta text-grey-blue-500 text-center text-[20px]">
              {t("cart.noItems")}
            </div>
          </div>
          <Button
            size="compact"
            onClick={() => navigate(`${routes.SEARCH}`)}
            endEnhancer={<Icon src="arrow_right_line" color="white" />}
          >
            {t("header.bookJourney")}
          </Button>
        </div>
      ) : (
        <>
          {cart &&
            cart.map((item: CartItem, index: number) => (
              <TravelComponent
                item={item}
                isEmptyingCart={isEmptyingCart}
                key={index}
              />
            ))}
        </>
      )}
    </div>
  );
}
